import { createStructuredSelector } from 'reselect';
import { ReactiveInjectable, reacts, injectable, inject } from 'src/features/ioc';
import type { WorkspaceWidgetDataModel } from 'src/models/WorkspaceWidgetDataModel';
import { container } from 'src/StaticContainer';
import type { RootState } from 'src/store';
import { onTabChannelChange, resetChannelData } from 'src/store/actions/widgetAndChannel';
import { userDoubleClickedSymbolFromTable } from 'src/store/actions/widgets/linking';
import type { ReactBindings } from 'src/types/bindings';

const makeSelectWidgetData = (tabId: string) =>
    createStructuredSelector({
        tab: (state: RootState) => state.tab.byId[tabId],
    });

type WidgetData = ReturnType<ReturnType<typeof makeSelectWidgetData>>;

const log = container.get('Logger').getSubLogger({ name: 'WorkspaceWidgetDataModel' });

@injectable()
export class WorkspaceWidgetDataModelImpl extends ReactiveInjectable implements WorkspaceWidgetDataModel {
    #widgetData!: WidgetData;

    constructor(@inject('Store') private store: ReactBindings['Store']) {
        super();
    }

    changeChannel(channelId: string | null) {
        this.store.dispatch(onTabChannelChange(this.tabId, channelId));
    }

    get channelId() {
        return this.#widgetData.tab.channelId;
    }

    get containerId() {
        return this.#widgetData.tab.containerId;
    }

    get displayName() {
        return this.#widgetData.tab.name || '';
    }

    init(tabId: string) {
        const selectWidgetData = makeSelectWidgetData(tabId);
        this.#widgetData = selectWidgetData(this.store.getState());

        const unsubscribe = this.store.subscribeSelector(selectWidgetData).handle((widgetData) => {
            this.widgetDataReactor = widgetData;
        });

        this.disposableStack.use({
            [Symbol.dispose]: () => {
                if (TRACE) {
                    log.trace({ message: 'disposing widget data store subscription' });
                }
                unsubscribe();
            },
        });
    }

    resetChannelData() {
        this.store.dispatch(resetChannelData(this.tabId));
    }

    get tabId() {
        return this.#widgetData.tab.id;
    }

    userSelectedSymbol(symbol: string) {
        this.store.dispatch(userDoubleClickedSymbolFromTable(this.tabId, symbol));
    }

    get widget() {
        return this.#widgetData.tab.widget;
    }

    @reacts set widgetDataReactor(widgetData: WidgetData) {
        this.#widgetData = widgetData;
    }
}
